import React, { useEffect, useRef } from 'react';

interface Bubble {
  x: number;
  y: number;
  size: number;
  speed: number;
  wobble: number;
  wobbleSpeed: number;
}

interface Seaweed {
  x: number;
  height: number;
  width: number;
  speed: number;
  offset: number;
}

interface Jelly {
  x: number;
  y: number;
  size: number;
  speed: number;
  offset: number;
  hue: number;
}

const SpongeBobBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameId = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const reducedMotionQuery =
      typeof window !== 'undefined'
        ? window.matchMedia?.('(prefers-reduced-motion: reduce)')
        : undefined;
    let reducedMotion = reducedMotionQuery?.matches ?? false;

    let bubbles: Bubble[] = [];
    let seaweeds: Seaweed[] = [];
    let jellies: Jelly[] = [];
    let time = 0;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      initScene();
      if (reducedMotion) renderStatic();
    };

    function sandY() {
      return canvas.height * 0.84;
    }

    function initScene() {
      bubbles = Array.from({ length: 30 }, () => spawnBubble());
      const count = 8;
      seaweeds = Array.from({ length: count }, (_, i) => ({
        x: (canvas.width / (count + 1)) * (i + 1) + (Math.random() - 0.5) * 50,
        height: Math.random() * 120 + 80,
        width: Math.random() * 8 + 6,
        speed: Math.random() * 0.02 + 0.01,
        offset: Math.random() * Math.PI * 2,
      }));
      jellies = Array.from({ length: 4 }, () => ({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height * 0.6 + canvas.height * 0.1,
        size: Math.random() * 18 + 16,
        speed: Math.random() * 0.4 + 0.2,
        offset: Math.random() * Math.PI * 2,
        hue: Math.random() > 0.5 ? 330 : 285,
      }));
    }

    function spawnBubble(): Bubble {
      return {
        x: Math.random() * canvas.width,
        y: canvas.height + Math.random() * 100,
        size: Math.random() * 8 + 3,
        speed: Math.random() * 1 + 0.4,
        wobble: Math.random() * Math.PI * 2,
        wobbleSpeed: Math.random() * 0.04 + 0.02,
      };
    }

    function drawWater() {
      const water = ctx.createLinearGradient(0, 0, 0, canvas.height);
      water.addColorStop(0, '#7fe9ff');
      water.addColorStop(0.5, '#33c4e8');
      water.addColorStop(0.85, '#1a96c4');
      water.addColorStop(1, '#0e7aa8');
      ctx.fillStyle = water;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    function drawLightRays() {
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      for (let i = 0; i < 4; i++) {
        const x = (canvas.width / 4) * i + Math.sin(time * 0.3 + i) * 30;
        ctx.fillStyle = 'rgba(255, 255, 220, 0.06)';
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x + 80, 0);
        ctx.lineTo(x + 160, canvas.height);
        ctx.lineTo(x + 40, canvas.height);
        ctx.closePath();
        ctx.fill();
      }
      ctx.restore();
    }

    function drawSand() {
      const base = sandY();
      const sand = ctx.createLinearGradient(0, base, 0, canvas.height);
      sand.addColorStop(0, '#f5e6a8');
      sand.addColorStop(1, '#e0c878');
      ctx.fillStyle = sand;
      ctx.beginPath();
      ctx.moveTo(0, canvas.height);
      ctx.lineTo(0, base);
      for (let x = 0; x <= canvas.width; x += 30) {
        const dune = Math.sin(x * 0.01 + 0.5) * 14;
        ctx.lineTo(x, base + dune);
      }
      ctx.lineTo(canvas.width, canvas.height);
      ctx.closePath();
      ctx.fill();
    }

    function drawPineapple() {
      const px = canvas.width * 0.82;
      const baseY = sandY() + 6;
      const w = 70;
      const h = 130;

      // Body
      ctx.fillStyle = 'rgba(245, 158, 11, 0.55)';
      ctx.beginPath();
      ctx.ellipse(px, baseY - h / 2, w / 2, h / 2, 0, 0, Math.PI * 2);
      ctx.fill();

      // Cross-hatch texture
      ctx.strokeStyle = 'rgba(180, 83, 9, 0.4)';
      ctx.lineWidth = 1.5;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(px - w / 2, baseY - h / 2 + i * 16);
        ctx.lineTo(px + w / 2, baseY - h / 2 + i * 16 + 14);
        ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(px - w / 2, baseY - h / 2 + i * 16 + 14);
        ctx.lineTo(px + w / 2, baseY - h / 2 + i * 16);
        ctx.stroke();
      }

      // Leafy crown
      ctx.fillStyle = 'rgba(22, 163, 74, 0.55)';
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(px, baseY - h + 10);
        ctx.lineTo(px + i * 14, baseY - h - 28);
        ctx.lineTo(px + i * 14 + 8, baseY - h + 10);
        ctx.closePath();
        ctx.fill();
      }
    }

    function drawSeaweed(s: Seaweed) {
      const segs = 8;
      ctx.strokeStyle = 'rgba(34, 139, 34, 0.5)';
      ctx.lineWidth = s.width;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(s.x, sandY());
      for (let i = 1; i <= segs; i++) {
        const t = i / segs;
        const sway = Math.sin(time * s.speed * 40 + s.offset + t * 3) * 20 * t;
        ctx.lineTo(s.x + sway, sandY() - s.height * t);
      }
      ctx.stroke();
    }

    function drawJelly(j: Jelly) {
      const wobble = Math.sin(time * 2 + j.offset) * 4;
      ctx.fillStyle = `hsla(${j.hue}, 80%, 75%, 0.5)`;
      ctx.beginPath();
      ctx.ellipse(j.x, j.y + wobble, j.size, j.size * 0.7, 0, Math.PI, 0);
      ctx.fill();
      // Tentacles
      ctx.strokeStyle = `hsla(${j.hue}, 80%, 70%, 0.5)`;
      ctx.lineWidth = 2;
      for (let i = -2; i <= 2; i++) {
        ctx.beginPath();
        ctx.moveTo(j.x + i * (j.size * 0.35), j.y + wobble);
        ctx.quadraticCurveTo(
          j.x + i * (j.size * 0.35) + Math.sin(time * 3 + i) * 4,
          j.y + wobble + j.size,
          j.x + i * (j.size * 0.35),
          j.y + wobble + j.size * 1.6
        );
        ctx.stroke();
      }
    }

    function updateJelly(j: Jelly) {
      j.x += Math.sin(time * 0.5 + j.offset) * 0.4;
      j.y -= j.speed * 0.15;
      if (j.y < -j.size * 2) {
        j.y = canvas.height * 0.7;
        j.x = Math.random() * canvas.width;
      }
    }

    function drawBubble(b: Bubble) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.arc(b.x, b.y, b.size, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.beginPath();
      ctx.arc(b.x - b.size * 0.3, b.y - b.size * 0.3, b.size * 0.25, 0, Math.PI * 2);
      ctx.fill();
    }

    function updateBubble(b: Bubble) {
      b.y -= b.speed;
      b.wobble += b.wobbleSpeed;
      b.x += Math.sin(b.wobble) * 0.6;
      if (b.y < -20) Object.assign(b, spawnBubble());
    }

    function renderStatic() {
      drawWater();
      drawLightRays();
      jellies.forEach(drawJelly);
      drawSand();
      drawPineapple();
      seaweeds.forEach(drawSeaweed);
      bubbles.forEach(drawBubble);
    }

    function animate() {
      if (reducedMotion) return;
      time += 0.01;
      drawWater();
      drawLightRays();
      jellies.forEach((j) => {
        updateJelly(j);
        drawJelly(j);
      });
      drawSand();
      drawPineapple();
      seaweeds.forEach(drawSeaweed);
      bubbles.forEach((b) => {
        updateBubble(b);
        drawBubble(b);
      });
      animationFrameId.current = requestAnimationFrame(animate);
    }

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    const handleReducedMotionChange = (event: MediaQueryListEvent) => {
      reducedMotion = event.matches;
      cancelAnimationFrame(animationFrameId.current);
      if (reducedMotion) {
        renderStatic();
      } else {
        animate();
      }
    };

    reducedMotionQuery?.addEventListener?.('change', handleReducedMotionChange);

    if (reducedMotion) {
      renderStatic();
    } else {
      animate();
    }

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      reducedMotionQuery?.removeEventListener?.('change', handleReducedMotionChange);
      cancelAnimationFrame(animationFrameId.current);
    };
  }, []);

  return <canvas ref={canvasRef} className="fixed inset-0 pointer-events-none z-0" />;
};

export default SpongeBobBackground;
